import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bookmark, Search, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MultiCombobox } from '../ui/multi-combobox';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import { DataTable, DataTableColumn } from '../ui/data-table';
import { getBookmarks, deleteBookmark } from '../../services/apiService';
import CopyButton from '../general/CopyButton';
import LoadingRing from '../ui-components/LoadingRing';
import Status from '../general-components/Status';
import { toTitleCase } from '../../utils/utils';

export interface BookmarkedImplant {
    _id: string;
    model: string;
    manufacturer: string;
    type: string;
    mriCode: string | null;
    mriStatus: number | null;
    regions: string[];
}

const BookmarksPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [bookmarks, setBookmarks] = useState<BookmarkedImplant[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [selectedManufacturers, setSelectedManufacturers] = useState<
        string[]
    >([]);

    useEffect(() => {
        const fetchBookmarks = async () => {
            const bookmarkList = await getBookmarks();
            setBookmarks(Array.isArray(bookmarkList) ? bookmarkList : []);
            setIsLoading(false);
        };
        fetchBookmarks();
    }, []);

    const allTypes = useMemo(
        () =>
            Array.from(
                new Set(bookmarks.map((b) => b.type).filter(Boolean))
            ).sort(),
        [bookmarks]
    );

    const allManufacturers = useMemo(
        () =>
            Array.from(
                new Set(
                    bookmarks
                        .map((b) => toTitleCase(b.manufacturer))
                        .filter(Boolean)
                )
            ).sort(),
        [bookmarks]
    );

    const visibleBookmarks = useMemo(() => {
        let result = bookmarks;

        if (selectedTypes.length > 0) {
            result = result.filter((b) => selectedTypes.includes(b.type));
        }

        if (selectedManufacturers.length > 0) {
            result = result.filter((b) =>
                selectedManufacturers.includes(toTitleCase(b.manufacturer))
            );
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(
                (b) =>
                    b.model?.toLowerCase().includes(term) ||
                    b.manufacturer?.toLowerCase().includes(term) ||
                    b.type?.toLowerCase().includes(term) ||
                    b.mriCode?.toLowerCase().includes(term)
            );
        }

        return result;
    }, [bookmarks, searchTerm, selectedTypes, selectedManufacturers]);

    const handleRemove = async (
        e: React.MouseEvent,
        bookmark: BookmarkedImplant
    ) => {
        e.stopPropagation();
        const response = await deleteBookmark(bookmark._id);
        if (response) {
            setBookmarks((prev) =>
                prev.filter((b) => b._id !== bookmark._id)
            );
        }
    };

    const columns: DataTableColumn<BookmarkedImplant>[] = [
        {
            key: 'model',
            header: t('bookmarks.table.headers.model'),
            cell: (b) => b.model,
            sortValue: (b) => b.model,
            className: 'font-medium',
        },
        {
            key: 'manufacturer',
            header: t('bookmarks.table.headers.manufacturer'),
            cell: (b) => toTitleCase(b.manufacturer),
            sortValue: (b) => b.manufacturer,
            className: 'text-muted-foreground',
        },
        {
            key: 'type',
            header: t('bookmarks.table.headers.type'),
            cell: (b) => b.type,
            sortValue: (b) => b.type,
            className: 'text-muted-foreground',
        },
        {
            key: 'mriCode',
            header: t('bookmarks.table.headers.mriCode'),
            cell: (b) =>
                b.mriCode ? (
                    <div className="flex items-center gap-1">
                        <span>{b.mriCode}</span>
                        <CopyButton value={b.mriCode} />
                    </div>
                ) : (
                    <span className="text-muted-foreground/50">—</span>
                ),
            sortValue: (b) => b.mriCode,
            className: 'text-muted-foreground',
        },
        {
            key: 'mriStatus',
            header: t('bookmarks.table.headers.mriStatus'),
            cell: (b) => <Status status={b.mriStatus ?? 0} />,
            sortValue: (b) => b.mriStatus ?? 0,
        },
        {
            key: 'actions',
            header: '',
            headClassName: 'w-16',
            cell: (b) => (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground/50 hover:text-muted-foreground"
                            onClick={(e) => handleRemove(e, b)}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t('bookmarks.removeBookmark')}
                    </TooltipContent>
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="container mx-auto max-w-6xl px-4 py-8">
            <div className="mb-6">
                <h1 className="text-2xl font-bold">{t('bookmarks.title')}</h1>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <LoadingRing size="large" />
                </div>
            ) : bookmarks.length === 0 ? (
                <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                    <Bookmark className="mb-3 h-10 w-10 text-muted-foreground" />
                    <h2 className="mb-1 text-lg font-semibold">
                        {t('bookmarks.noBookmarks')}
                    </h2>
                </div>
            ) : (
                <>
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                        <div className="relative w-full sm:max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder={t(
                                    'bookmarks.filters.searchPlaceholder'
                                )}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                        <MultiCombobox
                            options={allTypes}
                            value={selectedTypes}
                            onChange={setSelectedTypes}
                            placeholder={t(
                                'bookmarks.filters.typesPlaceholder'
                            )}
                            searchPlaceholder={t(
                                'bookmarks.filters.typesSearchPlaceholder'
                            )}
                            emptyText={t('bookmarks.filters.noTypes')}
                            className="w-full sm:w-56"
                            aria-label="Filter by type"
                        />
                        <MultiCombobox
                            options={allManufacturers}
                            value={selectedManufacturers}
                            onChange={setSelectedManufacturers}
                            placeholder={t(
                                'bookmarks.filters.manufacturersPlaceholder'
                            )}
                            searchPlaceholder={t(
                                'bookmarks.filters.manufacturersSearchPlaceholder'
                            )}
                            emptyText={t('bookmarks.filters.noManufacturers')}
                            className="w-full sm:w-56"
                            aria-label="Filter by manufacturer"
                        />
                    </div>

                    {visibleBookmarks.length === 0 ? (
                        <div className="mt-4 flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
                            <Search className="mb-3 h-10 w-10 text-muted-foreground" />
                            <p className="max-w-md text-sm text-muted-foreground">
                                {t('bookmarks.filters.noResults')}
                            </p>
                        </div>
                    ) : (
                        <TooltipProvider delayDuration={300}>
                            <DataTable
                                columns={columns}
                                items={visibleBookmarks}
                                rowKey={(b) => b._id}
                                onRowClick={(b) =>
                                    navigate('/implant/' + b._id)
                                }
                            />
                        </TooltipProvider>
                    )}
                </>
            )}
        </div>
    );
};

export default BookmarksPage;
